import {
  Box,
  Center,
  Flex,
  HStack,
  Spinner,
  Stack,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';

import AssignmentDataCard from '@/client/components/learners/dashboard/components/cards/AssignmentDataCard';
import ErrorAlert from '@/client/components/data-display/ErrorAlert';
import LearnerDashboardContentListItem from '@/client/components/learners/dashboard/components/lists/LearnerDashboardContentListItem';
import LearnerDashboardCustomTab from '@/client/components/learners/dashboard/components/tabs/LearnerDashboardCustomTab';
import NoAssignmentsPlaceholder from '@/client/components/learners/dashboard/components/assignments/NoAssignmentsPlaceholder';
import type { UserAssignment } from '@/client/types/dashboard/UserAssignment';
import { format } from 'date-fns';
import { setContentUrl } from '@/client/components/learners/dashboard/utils/setContentUrl';
import { useGetDashboardUserAssignments } from '@/client/components/learners/dashboard/hooks/useGetUserAssignments';
import { useLearnerDashboard } from '@/client/components/learners/dashboard/hooks/useLearnerDashboard';
import { useNavigate } from 'react-router-dom';
import { useSetCompletionStatus } from '@/client/components/learners/dashboard/hooks/useSetCompletionStatus';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function LearnerDashboardAssignments() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setCompletionStatus } = useSetCompletionStatus();
  const [selectedTab, setSelectedTab] = useState<'open' | 'completed'>('open');
  const { contentTypeTranslationMap } = useLearnerDashboard();

  const { data, isLoading, isError } = useGetDashboardUserAssignments();

  const selectedCountStyles = {
    marginLeft: 2,
    borderRadius: 'full',
    backgroundColor: 'whiteAlpha.500',
    paddingX: 2,
    paddingY: 1,
    fontSize: '10px',
    fontWeight: 600,
  };

  const unselectedCountStyles = {
    marginLeft: 2,
    borderRadius: 'full',
    backgroundColor: 'neutral.100',
    paddingX: 2,
    paddingY: 1,
    fontSize: '10px',
    fontWeight: 600,
    color: 'neutral.700',
  };

  if (isLoading) {
    return (
      <Center height="full">
        <Spinner />
      </Center>
    );
  }

  if (isError) {
    return (
      <ErrorAlert
        title={t('learners.dashboard.assignments.error')}
        backAction={{
          label: t('learners.dashboard.assignments.error.backToDashboard'),
          to: '/dashboard',
        }}
      />
    );
  }

  const { openAssignments, completedAssignments } = data;

  const sortAssignments = (assignments: typeof openAssignments) =>
    [...assignments].sort((a, b) => {
      // Handle cases where either assignment has no due date
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;

      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });

  const groupOpenAssignmentsByMonth = (assignments: typeof openAssignments) => {
    const groups = assignments.reduce((acc, assignment) => {
      // Check if assignment is overdue
      const isOverdue = assignment.due_date && new Date(assignment.due_date) < new Date();

      let monthYear;
      if (isOverdue) {
        monthYear = t('learners.dashboard.assignments.overdue');
      } else if (!assignment.due_date) {
        monthYear = t('learners.dashboard.noDueDate');
      } else {
        const dueDate = new Date(assignment.due_date);
        const currentDate = new Date();

        if (
          dueDate.getMonth() === currentDate.getMonth() &&
          dueDate.getFullYear() === currentDate.getFullYear()
        ) {
          monthYear = t('learners.dashboard.assignments.dueThisMonth');
        } else {
          monthYear = t('learners.dashboard.assignments.dueDate', {
            date: format(dueDate, 'MMMM yyyy'),
          });
        }
      }

      if (!acc[monthYear]) {
        Object.assign(acc, { [monthYear]: [] });
      }

      acc[monthYear].push(assignment);
      return acc;
    }, {} as Record<string, typeof openAssignments>);

    // Sort the groups to put Overdue first, then by date
    return Object.entries(groups)
      .sort(([keyA, _], [keyB, __]) => {
        if (keyA === 'Overdue') return -1;
        if (keyB === 'Overdue') return 1;
        if (keyA === 'Due This Month') return -1;
        if (keyB === 'Due This Month') return 1;
        if (keyA === t('learners.dashboard.noDueDate')) return 1;
        if (keyB === t('learners.dashboard.noDueDate')) return -1;

        const dateA = new Date(keyA.replace('Due ', ''));
        const dateB = new Date(keyB.replace('Due ', ''));
        return dateA.getTime() - dateB.getTime();
      })
      .reduce(
        (obj, [key, value]) => ({ ...obj, [key]: value }),
        {} as Record<string, typeof openAssignments>,
      );
  };

  const groupCompletedAssignmentsByMonth = (assignments: typeof completedAssignments) => {
    const groups = assignments.reduce((acc, assignment) => {
      let monthYear;

      if (!assignment.completed_date) {
        monthYear = t('learners.dashboard.noCompletedDate');
      } else {
        const completedDate = new Date(assignment.completed_date);
        const currentDate = new Date();

        if (
          completedDate.getMonth() === currentDate.getMonth() &&
          completedDate.getFullYear() === currentDate.getFullYear()
        ) {
          monthYear = t('learners.dashboard.assignments.completedThisMonth');
        } else {
          monthYear = t('learners.dashboard.assignments.completedDate', {
            date: format(completedDate, 'MMMM yyyy'),
          });
        }
      }

      if (!acc[monthYear]) {
        Object.assign(acc, { [monthYear]: [] });
      }

      acc[monthYear].push(assignment);
      return acc;
    }, {} as Record<string, typeof completedAssignments>);

    // Sort groups in reverse chronological order
    const sorted = Object.entries(groups)
      .sort(([keyA, _], [keyB, __]) => {
        // Always keep "Completed this month" first
        if (keyA === t('learners.dashboard.assignments.completedThisMonth')) return -1;
        if (keyB === t('learners.dashboard.assignments.completedThisMonth')) return 1;

        // Always keep "No completed date" last
        if (keyA === t('learners.dashboard.noCompletedDate')) return 1;
        if (keyB === t('learners.dashboard.noCompletedDate')) return -1;

        // Extract dates from the "Completed Month YYYY" format and compare
        const dateA = new Date(keyA.replace('Completed ', ''));
        const dateB = new Date(keyB.replace('Completed ', ''));
        return dateB.getTime() - dateA.getTime(); // Reverse chronological
      })
      .reduce(
        (obj, [key, value]) => ({ ...obj, [key]: value }),
        {} as Record<string, typeof completedAssignments>,
      );

    return sorted;
  };

  const sortedOpenAssignments = sortAssignments(openAssignments);
  const groupedOpenAssignments = groupOpenAssignmentsByMonth(sortedOpenAssignments);

  const sortedCompletedAssignments = sortAssignments(completedAssignments);
  const groupedCompletedAssignments = groupCompletedAssignmentsByMonth(sortedCompletedAssignments);

  const getAssignmentRenderData = (assignment: UserAssignment) => {
    const imageUrl =
      assignment.assigned_content[0].content_id?.images?.explore ||
      assignment.assigned_content[0].content_id.image;

    const multipleAssignedContent = assignment.assigned_content.length > 1;

    const title = multipleAssignedContent
      ? t('learners.dashboard.assignments.groupOfItems', {
          count: assignment.assigned_content.length,
        })
      : assignment.assigned_content[0].content_id.title ||
        assignment.assigned_content[0].content_id.name;

    const typeLabel = multipleAssignedContent
      ? `${assignment.completed_content.length} / ${assignment.assigned_content.length} completed`
      : contentTypeTranslationMap[assignment.assigned_content[0].content_id.type];

    const onClick = multipleAssignedContent
      ? () => navigate('assignment-details', { state: { assignment } })
      : () =>
          navigate(
            setContentUrl(
              assignment.assigned_content[0].content_id._id,
              assignment.assigned_content[0].content_id.type,
              assignment.assigned_content[0].user_content_id,
            ),
          );

    const statusData = setCompletionStatus(assignment);

    return { imageUrl, title, typeLabel, onClick, statusData };
  };

  return (
    <Tabs>
      <Box
        position="sticky"
        top={0}
        left={0}
        right={0}
        zIndex={1}
        paddingY={4}
        backgroundColor="white"
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        width={{ base: '100vw', md: 'full' }}
        marginLeft={{ base: 'calc(-50vw + 50%)', md: 0 }}
        paddingX={{ base: 4, md: 0 }}
      >
        <TabList>
          <LearnerDashboardCustomTab
            display="flex"
            alignItems="center"
            onClick={() => setSelectedTab('open')}
            scrollToTop
          >
            {`${t('learners.dashboard.assignments.open')} `}
            <Box sx={selectedTab === 'open' ? selectedCountStyles : unselectedCountStyles}>
              {openAssignments.length}
            </Box>
          </LearnerDashboardCustomTab>

          {completedAssignments.length > 0 && (
            <LearnerDashboardCustomTab
              display="flex"
              alignItems="center"
              onClick={() => setSelectedTab('completed')}
              scrollToTop
            >
              {`${t('learners.dashboard.assignments.completed')}`}
              <Box sx={selectedTab === 'completed' ? selectedCountStyles : unselectedCountStyles}>
                {completedAssignments.length}
              </Box>
            </LearnerDashboardCustomTab>
          )}
        </TabList>
      </Box>

      <TabPanels>
        <TabPanel padding={0}>
          <Stack spacing={8}>
            {Object.entries(groupedOpenAssignments).length === 0 && <NoAssignmentsPlaceholder />}

            {Object.entries(groupedOpenAssignments).map(([dueDate, assignments]) => (
              <Stack key={dueDate} spacing={8}>
                <Text fontSize="18px" fontWeight={600} color="baseBlack">
                  {dueDate}
                </Text>

                <HStack
                  spacing={4}
                  gap={4}
                  flexWrap="wrap"
                  justifyContent={{ base: 'center', md: 'flex-start' }}
                >
                  {assignments.map((assignment) => (
                    <Box key={assignment._id} width={{ base: 'full', md: '30%', lg: '250px' }}>
                      <AssignmentDataCard key={assignment._id} assignment={assignment} />
                    </Box>
                  ))}
                </HStack>
              </Stack>
            ))}
          </Stack>
        </TabPanel>

        <TabPanel padding={0}>
          <Stack spacing={8}>
            {Object.entries(groupedCompletedAssignments).map(([dueDate, assignments]) => (
              <Box key={dueDate}>
                <Text fontSize="18px" fontWeight={600} color="baseBlack">
                  {dueDate}
                </Text>

                {assignments.map((assignment, index) => {
                  const { title, typeLabel, imageUrl, onClick, statusData } =
                    getAssignmentRenderData(assignment);

                  return assignment.assigned_content.length > 1 ? (
                    <Box key={assignment._id}>
                      <LearnerDashboardContentListItem
                        title={`${assignment.assigned_content.length} Item Assignment`}
                        typeLabel=""
                        renderBottomBorder={false}
                        imageUrl={imageUrl}
                        statusData={statusData}
                        onClick={onClick}
                      />

                      <Box paddingLeft={10} borderBottom="1px solid" borderColor="neutral.100">
                        {assignment.assigned_content.map((content) => {
                          // TODO: Why aren't archived assignments filtered out at the API level?
                          if (!content.content_id || content.content_id.archived) return;

                          return (
                            <Flex key={content.content_id._id} width="full">
                              <Box
                                height="40px"
                                width="20px"
                                marginRight={4}
                                borderBottom="2px solid"
                                borderLeft="2px solid"
                                borderBottomLeftRadius="4px"
                                borderColor="neutral.100"
                              />

                              <Box flex={1}>
                                <LearnerDashboardContentListItem
                                  title={content.content_id.title}
                                  typeLabel={contentTypeTranslationMap[content.content_id.type]}
                                  renderBottomBorder={false}
                                  imageUrl={content.content_id.image}
                                  statusData={{ statusText: 'Completed', status: 'completed' }}
                                  onClick={onClick}
                                />
                              </Box>
                            </Flex>
                          );
                        })}
                      </Box>
                    </Box>
                  ) : (
                    <LearnerDashboardContentListItem
                      key={assignment._id}
                      title={title}
                      typeLabel={typeLabel}
                      renderBottomBorder={index !== assignments.length - 1}
                      imageUrl={imageUrl}
                      statusData={statusData}
                      onClick={onClick}
                    />
                  );
                })}
              </Box>
            ))}
          </Stack>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}
